import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { forkJoin, Subject, take, takeUntil } from 'rxjs';
import { CommonDataService } from 'src/sb-tools/typescript/services/common-data.service';
import { LeftMenuItemModel } from '@sb-tools/typescript/models/left-menu-item.model';
import { FolderService } from 'src/app/core/services/ticket/folder.service';
import * as Tinycon from 'Tinycon';
import { TicketSource } from '@sb-tools/typescript/enums/ticket-source.enum';
import { BsModalService } from 'ngx-bootstrap/modal';
import { SignalRService } from 'src/app/core/services/signalR.service';
import { NewTicketModalComponent } from '../../shared/modal-windows/new-ticket-modal/new-ticket-modal.component';
import { ShortNumberPipe } from '../../shared/pipes/short-number.pipe';
import { UserService } from 'src/sb-tools/typescript/services/user.service';
import { FieldsSearchService } from '@sb-tools/typescript/services/fields-search.service';
import { SearchTicketsService } from '@sb-tools/typescript/services/search-tickets.service';
import { FieldSearchModel } from '@sb-tools/typescript/models/field-search.model';
import { TagService } from '@sb-tools/typescript/services/tag.service';
import { UserRole } from 'src/sb-tools/typescript/enums/user-role.enum';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SocialMediaService } from '../../core/services/social-media.service';
import { TicketRightPanelService } from '../../core/services/ticket-right-panel.service';
import { TicketFolder } from '@sb-tools/typescript/enums/ticket-folder.enum';
import { TicketStatus } from '@sb-tools/typescript/enums/ticket-status.enum';
import { CustomFiltersService } from '../../core/services/custom-filters.service';
import { CustomFilterGroupDTO } from '@sb-tools/typescript/models/dto/custom-filter-group.dto';
import { FilterTooltipTextPipe } from '../../shared/pipes/filter-tooltip-text.pipe';
import { environment } from '@environment';
import { CustomFilterDTO } from '@sb-tools/typescript/models/dto/custom-filter.dto';
import { FolderCountsUpdatedEventModel } from '../../core/models/dto/signalR/folder-counts-updated-event-model.model';
import { MainCountsModel } from '../../core/models/main-counts-model.model';
import { UserDataService } from '@sb-tools/typescript/services/user-data.service';
import { UserGroupDTO } from '@sb-tools/typescript/models/dto/user-group.dto';
import { BaseUserWithRole } from '@sb-tools/typescript/models/base-user-with-role.model';
import { CommunitySwitcherComponent } from './community-switcher/community-switcher.component';
import { InlineSVGComponent } from '../../../sb-modules/Frontend-Inline-SVG-Module/inline-svg.component';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { NgClass } from '@angular/common';
import { LeftMenuItemComponent } from './left-menu-item/left-menu-item.component';
import { InboxElementsVisibility } from '@sb-tools/typescript/models/inbox-elements-visibility.model';
import { isInboxElementVisible } from '../../core/utils/is-inbox-element-visible.util';

type LeftMenuItemWithVisibilityTestId = LeftMenuItemModel & {
    visibilityTestId?: string;
};

@Component({
    selector: 'app-left-menu',
    templateUrl: './left-menu.component.html',
    styleUrls: ['./left-menu.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommunitySwitcherComponent,
        InlineSVGComponent,
        RouterLinkActive,
        RouterLink,
        TranslateModule,
        NgClass,
        LeftMenuItemComponent,
    ],
})
export class LeftMenuComponent implements OnInit, OnDestroy {
    allFields: FieldSearchModel[] = [];
    communityName;
    countUpdateMarker;
    folderStat = {};
    isBotTicketsWithOpen = false;
    leftMenuBaseItems: LeftMenuItemWithVisibilityTestId[];
    leftMenuFiltersItems: LeftMenuItemModel[];
    readonly isInboxElementVisible = isInboxElementVisible;
    ticketSourceEnum = TicketSource;
    ticketStatusEnum = TicketStatus;
    userGroups: UserGroupDTO[];
    users: BaseUserWithRole[] = [];

    private folderUpdateCount = 0;
    private timerId;
    private unsubscriber = new Subject<void>();

    constructor(
        public commonDataService: CommonDataService,
        private fieldsSearchService: FieldsSearchService,
        private folderService: FolderService,
        private modalService: BsModalService,
        private customFiltersService: CustomFiltersService,
        private searchTicketsService: SearchTicketsService,
        private signalRService: SignalRService,
        private shortNumberPipe: ShortNumberPipe,
        private tagService: TagService,
        private translateService: TranslateService,
        private userService: UserService,
        private userDataService: UserDataService,
        private ticketRightPanelService: TicketRightPanelService,
        private changeDetectorRef: ChangeDetectorRef,
        private socialMediaService: SocialMediaService,
        private filterTooltipTextPipe: FilterTooltipTextPipe,
    ) {}

    ngOnInit(): void {
        this.communityName = this.commonDataService.communityName;
        this.folderStat = this.commonDataService.folderStat;

        this.loadFolderCounters();

        this.signalRService
            .onFolderCountersUpdated()
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((data: FolderCountsUpdatedEventModel) => {
                this.folderUpdateCount++;
                console.log('%c onSetNewFoldersCounts: ' + this.folderUpdateCount, 'color: purple');
                this.processFolderCounters(data.folderCounts);
                this.updateFolderCounters(data.folderCounts);
            });

        this.socialMediaService.get().subscribe((statuses) => {
            this.commonDataService.socialMediaStatuses = statuses;

            this.commonDataService.socialMediaStatusesLoaded = true;

            this.initMenuItems();
        });

        Tinycon.setOptions({
            background: '#f03d25',
            color: '#ffffff',
        });

        if (!this.commonDataService.user.isReadOnly && isInboxElementVisible(this.getLeftPanelSettings()?.filters)) {
            this.commonDataService.hasVisibleLeftMenuFilters = undefined;
            this.initFiltersMenu();
        } else {
            this.commonDataService.hasVisibleLeftMenuFilters = false;
        }
    }

    checkLinkClick(event): void {
        if (!this.ticketRightPanelService.isNeedToDiscardUnsavedChanges()) {
            event.stopPropagation();
            event.preventDefault();
            event.cancelBubble = true;
            event.stopImmediatePropagation();
        }
    }

    initFiltersMenu(): void {
        forkJoin([
            this.customFiltersService.getAll(),
            this.userService.getAll().pipe(take(1)),
            this.searchTicketsService.getAvailableFields(),
            this.userDataService.getUserGroups(),
        ])
            .pipe(takeUntil(this.unsubscriber))
            .subscribe(([filterGroups, users, fields, userGroups]) => {
                this.users = users;
                this.allFields = fields;
                this.userGroups = userGroups;
                this.formMenuFiltersItems(filterGroups);
            });
    }

    private formMenuFiltersItems(filterGroups: CustomFilterGroupDTO[]): void {
        this.leftMenuFiltersItems = filterGroups.reduce((rootMenuItems, group) => {
            const rootMenuItem: LeftMenuItemModel = {
                title: group.name,
                level: 1,
                isItemWithoutLink: true,
                withEditButton: true,
                editButtonTooltipText: this.translateService.instant('Edit a filter'),
                editButtonClickHandler: () => {
                    window.open(
                        environment.settingsUrl + this.commonDataService.communityName + '/user/filters',
                        '_blank',
                    );
                },
                filterGroupId: group.id,
                hasNoCountProperties: true,
                folderNames: ['filterGroups', group.id.toString(), 'totalCount'],
            };

            rootMenuItem.children = group.filters.reduce((childMenuItems, filter) => {
                const params = this.getFilterParams(filter);

                const filterForPipe = {
                    originalModel: filter,
                    processedModel: {
                        name: filter.name,
                        params,
                        isCurrentUser: true,
                        isEditable: this.commonDataService.user.role === UserRole.admin,
                    },
                };

                const filterDescription = this.filterTooltipTextPipe.transform(
                    filterForPipe,
                    this.tagService.allTags,
                    this.users,
                    this.allFields,
                    this.userGroups,
                );

                const childMenuItem = {
                    folderNames: ['filterGroups', group.id.toString(), filter.id.toString()],
                    queryParams: params,
                    title: filter.name,
                    level: 2,
                    hasNoCountProperties: true,
                    filterDescription,
                };

                childMenuItems.push(childMenuItem);
                return childMenuItems;
            }, []);

            rootMenuItems.push(rootMenuItem);
            return rootMenuItems;
        }, []);
        this.commonDataService.hasVisibleLeftMenuFilters = this.leftMenuFiltersItems.length > 0;

        this.changeDetectorRef.detectChanges();
    }

    getCount(folderNames, countProperty: 'totalCount' | 'unhandledRepliesCount' = 'totalCount') {
        let count;

        if (this.commonDataService.folderStat && folderNames) {
            try {
                count = this.commonDataService.folderStat[folderNames[0]];

                for (let i = 1; i < folderNames.length; ++i) {
                    count = count[folderNames[i]];
                }

                return this.shortNumberPipe.transform(count[countProperty]);
            } catch {
                return 0;
            }
        } else {
            return 0;
        }
    }

    openNewTicketModal(): void {
        const initialState = {};

        this.modalService.show(NewTicketModalComponent, {
            initialState,
            class: 'modal-lg new-ticket-modal',
            ignoreBackdropClick: true,
        });
    }

    private getFilterParams(filter: CustomFilterDTO) {
        const params: any = {};

        params.filterId = filter.id;

        if (filter.adminView !== null) {
            params.adminView = filter.adminView;
            params.onlyMy = !filter.adminView;
        }

        if (filter.userWithSharedAccess !== null) {
            params.userWithSharedAccess = filter.userWithSharedAccess;
        }

        if (filter.categoryIds?.length) {
            params.categoryIds = filter.categoryIds.join(',');
        }

        if (filter.createdBy !== null) {
            params.createdBy = filter.createdBy;
        }

        if (filter.draftedBy !== null) {
            params.draftedBy = filter.draftedBy;
        }

        if (filter.folder !== null) {
            params.folder = filter.folder;
        }

        if (filter.status !== null) {
            params.status = filter.status;
        }

        if (filter.hasAttachments !== null) {
            params.hasAttachments = filter.hasAttachments;
        }

        if (filter.hasNoReplyFromTeam) {
            params.hasNoReplyFromTeam = filter.hasNoReplyFromTeam;
        }

        if (filter.withoutCategory !== null) {
            params.withoutCategory = filter.withoutCategory;
        }

        if (filter.isAssignedToBot !== null) {
            params.isAssignedToBot = filter.isAssignedToBot;
        }

        if (filter.isOnlyHelperCategories !== null) {
            params.isOnlyHelperCategories = filter.isOnlyHelperCategories;
        }

        if (filter.orderBy !== null) {
            params.orderBy = null;
        }

        if (filter.sentiment !== null) {
            params.sentiment = filter.sentiment;
        }

        if (filter.sourceSubtype !== null) {
            params.sourceSubtype = filter.sourceSubtype;
        }

        if (filter.source !== null) {
            params.source = filter.source;
        }

        if (filter.tagIds && filter.tagIds.length) {
            params.tagIds = filter.tagIds.join(',');
        }

        if (filter.fields && filter.fields.length) {
            params.fields = this.fieldsSearchService.getEncodedValue(filter.fields);
        }

        if (filter.isAssigned !== null) {
            params.isAssigned = filter.isAssigned;
        }

        if (filter.assignees && filter.assignees.length) {
            params.assignees = filter.assignees.join(',');
        }

        if (filter.assigneeUserGroupIds && filter.assigneeUserGroupIds.length) {
            params.assigneeUserGroupIds = filter.assigneeUserGroupIds.join(',');
        }

        if (filter.creationDateFilter !== null) {
            params.creationDateFilterType = filter.creationDateFilter.type;
            params.creationDateFilterFrom = filter.creationDateFilter.from;
            params.creationDateFilterTo = filter.creationDateFilter.to;
            params.creationDateFilterUnit = filter.creationDateFilter.unit;
            params.creationDateFilterValue = filter.creationDateFilter.value;
        }

        return params;
    }

    private getAllSources(params, folderName): LeftMenuItemModel[] {
        const sources = [];

        if (this.commonDataService.socialMediaStatuses.chatTickets) {
            sources.push({
                folderNames: [folderName, 'open', 'chat'],
                queryParams: { source: TicketSource.chat, ...params },
                title: this.translateService.instant('Chat'),
                level: 2,
            });
        }

        if (this.commonDataService.socialMediaStatuses.formTickets) {
            sources.push({
                folderNames: [folderName, 'open', 'form'],
                queryParams: { source: TicketSource.form, ...params },
                title: this.translateService.instant('Form'),
                level: 2,
            });
        }

        if (this.commonDataService.socialMediaStatuses.emailTickets) {
            sources.push({
                folderNames: [folderName, 'open', 'email'],
                queryParams: { source: TicketSource.email, ...params },
                title: this.translateService.instant('Email'),
                level: 2,
            });
        }

        if (this.commonDataService.socialMediaStatuses.apiTickets) {
            sources.push({
                folderNames: [folderName, 'open', 'api'],
                queryParams: { source: TicketSource.api, ...params },
                title: this.translateService.instant('API'),
                level: 2,
            });
        }

        if (
            this.commonDataService.socialMediaStatuses.facebookTickets ||
            this.commonDataService.socialMediaStatuses.facebook
        ) {
            sources.push({
                folderNames: [folderName, 'open', 'facebook'],
                queryParams: { source: TicketSource.facebook, ...params },
                title: this.translateService.instant('Facebook'),
                level: 2,
            });
        }

        if (
            this.commonDataService.socialMediaStatuses.instagramTickets ||
            this.commonDataService.socialMediaStatuses.instagram
        ) {
            sources.push({
                folderNames: [folderName, 'open', 'instagram'],
                queryParams: { source: TicketSource.instagram, ...params },
                title: this.translateService.instant('Instagram'),
                level: 2,
            });
        }

        if (
            this.commonDataService.socialMediaStatuses.youtubeTickets ||
            this.commonDataService.socialMediaStatuses.youtube
        ) {
            sources.push({
                folderNames: [folderName, 'open', 'youtube'],
                queryParams: { source: TicketSource.youtube, ...params },
                title: this.translateService.instant('YouTube'),
                level: 2,
            });
        }

        if (
            this.commonDataService.socialMediaStatuses.googleMyBusinessTickets ||
            this.commonDataService.socialMediaStatuses.googleMyBusiness
        ) {
            sources.push({
                folderNames: [folderName, 'open', 'googleMyBusiness'],
                queryParams: { source: TicketSource.googleMyBusiness, ...params },
                title: this.translateService.instant('Google'),
                level: 2,
            });
        }

        if (
            this.commonDataService.socialMediaStatuses.trustpilotTickets ||
            this.commonDataService.socialMediaStatuses.trustpilot
        ) {
            sources.push({
                folderNames: [folderName, 'open', 'trustpilot'],
                queryParams: { source: TicketSource.trustpilot, ...params },
                title: this.translateService.instant('Trustpilot'),
                level: 2,
            });
        }

        if (
            this.commonDataService.socialMediaStatuses.googlePlayMarket ||
            this.commonDataService.socialMediaStatuses.googlePlayMarketTickets
        ) {
            sources.push({
                folderNames: [folderName, 'open', 'googlePlayMarket'],
                queryParams: { source: TicketSource.googlePlayMarket, ...params },
                title: this.translateService.instant('Google Play'),
                level: 2,
            });
        }

        if (
            this.commonDataService.socialMediaStatuses.appStore ||
            this.commonDataService.socialMediaStatuses.appStoreTickets
        ) {
            sources.push({
                folderNames: [folderName, 'open', 'appStore'],
                queryParams: { source: TicketSource.appStore, ...params },
                title: this.translateService.instant('App Store'),
                level: 2,
            });
        }

        if (
            this.commonDataService.socialMediaStatuses.whatsApp ||
            this.commonDataService.socialMediaStatuses.whatsAppTickets
        ) {
            sources.push({
                folderNames: [folderName, 'open', 'whatsApp'],
                queryParams: { source: TicketSource.whatsApp, ...params },
                title: this.translateService.instant('WhatsApp'),
                level: 2,
            });
        }

        if (
            this.commonDataService.socialMediaStatuses.tikTok ||
            this.commonDataService.socialMediaStatuses.tikTokTickets
        ) {
            sources.push({
                folderNames: [folderName, 'open', 'tikTok'],
                queryParams: { source: TicketSource.tikTok, ...params },
                title: this.translateService.instant('TikTok'),
                level: 2,
            });
        }

        return sources;
    }

    private initMenuItems(): void {
        this.leftMenuBaseItems = [];
        const isInboxTicketsVisible = isInboxElementVisible(this.getLeftPanelSettings()?.inboxView);

        if (isInboxTicketsVisible) {
            this.leftMenuBaseItems.push({
                folderNames: ['myTickets'],
                queryParams: { onlyMy: true, status: TicketStatus.open },
                title: this.translateService.instant('Inbox'),
                level: 1,
                children: this.getAllSources({ onlyMy: true, status: TicketStatus.open }, 'myTickets'),
                icon: '/sb-tools/theme/images/Svg icons WEB/Web icons/Ticket icon.svg',
                class: 'my-tickets',
                visibilityTestId: 'inbox-ui-left-sidebar-inbox-tickets-view',
            });
        }

        if (isInboxTicketsVisible && this.commonDataService.user.role !== UserRole.readOnlyUser) {
            this.leftMenuBaseItems.push({
                folderNames: ['myTickets', 'withDrafts'],
                queryParams: {
                    onlyMy: false,
                    folder: TicketFolder.draft,
                    status: TicketStatus.open,
                },
                title: this.translateService.instant('Drafts'),
                level: 1,
                icon: '/sb-tools/theme/images/Svg icons WEB/Web icons/Pen icon.svg',
                class: 'my-tickets',
                hasNoCountProperties: true,
            });

            this.leftMenuBaseItems.push({
                folderNames: ['myTickets', 'assignedToMe'],
                queryParams: {
                    onlyMy: true,
                    folder: TicketFolder.assignedToMe,
                    status: TicketStatus.open,
                },
                title: this.translateService.instant('Assigned to me'),
                level: 1,
                icon: '/sb-tools/theme/images/Svg icons WEB/Web icons/Lockd icon TEST V02.svg',
                class: 'my-tickets',
                hasNoCountProperties: true,
            });

            this.leftMenuBaseItems.push({
                folderNames: ['answeredByMe'],
                queryParams: {
                    onlyMy: false,
                    repliedBy: this.commonDataService.user.id,
                },
                title: this.translateService.instant('Answered by me'),
                level: 1,
                icon: '/sb-tools/theme/images/Svg icons WEB/Web icons/Reply icon.svg',
                class: 'my-tickets',
                hasNoCountProperties: true,
            });
        }

        if (isInboxTicketsVisible) {
            this.leftMenuBaseItems.push({
                folderNames: ['myTickets', 'sharedAccess'],
                queryParams: {
                    onlyMy: true,
                    folder: TicketFolder.sharedAccess,
                    status: TicketStatus.open,
                },
                title: this.translateService.instant('Shared access'),
                level: 1,
                icon: '/sb-tools/theme/images/Svg icons WEB/Web icons/Forward icon.svg',
                class: 'my-tickets',
                hasNoCountProperties: true,
            });

            this.leftMenuBaseItems.push({
                folderNames: ['myTickets', 'reminderByMe'],
                queryParams: {
                    folder: TicketFolder.reminders,
                    status: TicketStatus.all,
                },
                title: this.translateService.instant('Reminders'),
                level: 1,
                icon: '/sb-tools/theme/images/Svg icons WEB/Web icons/Timer outlined icon.svg',
                class: 'my-tickets reminder-badge',
                hasNoCountProperties: true,
            });
        }

        if (
            (this.commonDataService.user.role === UserRole.admin ||
                this.commonDataService.user.role === UserRole.manager) &&
            isInboxElementVisible(this.getLeftPanelSettings()?.allTicketsView)
        ) {
            this.leftMenuBaseItems.push({
                folderNames: ['allTickets'],
                queryParams: { status: TicketStatus.open, onlyMy: false },
                title: this.translateService.instant('All tickets'),
                level: 1,
                children: this.getAllSources({ status: TicketStatus.open, onlyMy: false }, 'allTickets'),
                icon: '/sb-tools/theme/images/Svg icons WEB/Web icons/Ticket icon.svg',
                visibilityTestId: 'inbox-ui-left-sidebar-all-tickets-view',
            });
        }

        if (
            this.commonDataService.user.hasHelperCategories &&
            isInboxElementVisible(this.getLeftPanelSettings()?.helperTicketsView)
        ) {
            this.leftMenuBaseItems.push({
                folderNames: ['helperTickets', 'open'],
                queryParams: { isOnlyHelperCategories: true, status: TicketStatus.open },
                title: this.translateService.instant('Helper tickets'),
                level: 1,
                children: this.getAllSources(
                    { isOnlyHelperCategories: true, status: TicketStatus.open },
                    'helperTickets',
                ),
                icon: '/sb-tools/theme/images/Svg icons WEB/Web icons/Ticket icon.svg',
                visibilityTestId: 'inbox-ui-left-sidebar-helper-tickets-view',
            });
        }

        this.changeDetectorRef.detectChanges();
    }

    private updateFolderCounters(folderStat: MainCountsModel): void {
        if (this.commonDataService.user.isAdmin) {
            this.commonDataService.folderStat.allTickets = folderStat.allTickets;
            Tinycon.setBubble(this.getCount(['allTickets', 'open']), true);
        } else {
            Tinycon.setBubble(this.getCount(['myTickets', 'open']), true);
        }

        if (this.commonDataService.customerSettings.isBotEnabled) {
            this.commonDataService.folderStat.botTickets = folderStat.botTickets;

            clearTimeout(this.timerId);
            this.timerId = setTimeout(() => {
                this.isBotTicketsWithOpen = !!this.getCount(['botTickets', 'open']);
            }, 300);
        }

        this.countUpdateMarker = new Date().getTime();
    }

    private getLeftPanelSettings(): InboxElementsVisibility['leftPanel'] | undefined {
        return this.commonDataService.customerSettings?.inboxElementsVisibility?.leftPanel;
    }

    private loadFolderCounters(): void {
        this.folderService
            .get()
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((data) => {
                this.processFolderCounters(data);
            });
    }

    private processFolderCounters(folderCounters: MainCountsModel): void {
        this.commonDataService.folderStat = folderCounters;

        if (this.commonDataService.user.isAdmin) {
            Tinycon.setBubble(this.getCount(['allTickets', 'open']), true);
        } else {
            Tinycon.setBubble(this.getCount(['myTickets', 'open']), true);
        }

        if (this.commonDataService.customerSettings.isBotEnabled) {
            this.isBotTicketsWithOpen = !!this.getCount(['botTickets', 'open']);
        }

        this.countUpdateMarker = new Date().getTime();
        this.changeDetectorRef.detectChanges();
    }

    ngOnDestroy(): void {
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}
